/**
 * alerts.js - Shared safety alert analysis for dive plan segments.
 *
 * Checks 10 conditions: hypoxia, hyperoxia, END, gas density, narcosis
 * increase (bailout vs CCR), END increase, ICD risk, and decompression
 * ceiling violation. Also relays the plan level warnings raised by the
 * engine (result.warnings) as errors.
 *
 * Severity policy: 'error' is reserved for conditions under which the
 * displayed plan must not be dived as is (unbreathable gas, ascent above
 * the deco ceiling, incomplete plan relayed from result.warnings). Graded
 * risk factors (END, gas density, ICD, bailout narcosis) are 'warning':
 * a recommended threshold is exceeded, not a certainty of accident. The
 * distinction drives the red vs orange rendering everywhere alerts show.
 *
 * Used by both PlanView (screen display) and PdfExport (PDF/HTML output).
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 * AGPL-3.0-or-later - see LICENSE
 */

import { Gas } from '../core/Gas.js'
import { DivePlanner } from '../core/DivePlanner.js'
import {
  GAS_DENSITY_CRITICAL, GAS_DENSITY_WARNING,
  segmentDensity, segmentDepth, segmentMix
} from './planUtils.js'

// Tolerance (in meters) applied when comparing a segment ceiling to the depth
// the diver actually holds. Both numbers come from floating point arithmetic:
// the ceiling is derived from tissue pressures through several divisions and
// a water column conversion, the depth from rate * time sums. A ceiling equal
// to the depth therefore routinely lands a few 1e-13 m above or below it, and
// a strict `ceiling > depth` test would fire on that noise alone. 0.1 m is one
// order of magnitude below the finest quantity the plan displays (stop depths
// are whole meters) so no real violation can hide under it.
//
// Taken from the engine and not redefined here: the planner uses the very same
// value to decide that a dive level is reachable without decompressing first.
// Two different tolerances would let the UI flag a plan the engine considers
// legal, or stay silent on one it does not.
const CEILING_TOLERANCE_M = DivePlanner.CEILING_TOLERANCE_M

// Tolerance (in meters) used to decide that two segments sit at the same depth.
// Deco stop depths are produced by the same multiple of decoStopInterval, so
// they only ever differ by floating point noise when they are meant to match.
const SAME_DEPTH_EPSILON_M = 0.001

// ppO2 above which the plan is flagged as critically hyperoxic, in bar.
//
// Derived from Gas.MOD_TOLERANCE rather than hard-coded at 1.6: the planner
// deliberately holds a gas down to MOD_TOLERANCE metres past its exact MOD so
// that deco gases keep their conventional depths (pure O2 at 6 m). The alert
// has to grant the planner the slack the planner grants itself, otherwise it
// contradicts it on every plan. The worst case is the densest water offered,
// which is what SALINITY_MAX is for.
const HYPEROXIA_LIMIT_BAR = 1.6 + Gas.MOD_TOLERANCE * Gas.specificWeight(Gas.SALINITY_MAX)

// Prefix shared by every i18n key of PLAN_WARNING_CODES. Stripping it turns an
// engine warning code into the internal alert code used by the UI, exactly as
// the nine segment alerts already name themselves (hypoxia, end_high, ...).
const WARNING_KEY_PREFIX = 'alert_'

// Helium fraction lost, in absolute fraction points, above which a change of
// breathing mix is reported as an isobaric counterdiffusion risk. 0.20 is the
// value this check has carried since it was written; what changed is that it
// now has a chance to fire (see check 9).
export const ICD_HELIUM_DROP = 0.20

/**
 * Depth a segment ceiling has to be compared against: the depth the diver
 * holds at the end of the segment. Transitions (ascent/descent) are named by
 * their end depth, every other segment type by its single depth.
 *
 * @param {Object} segment - Plan segment
 * @returns {number|undefined} Reference depth in meters
 */
function ceilingReferenceDepth(segment) {
  if (segment.type === 'ascent' || segment.type === 'descent') return segment.endDepth
  return segment.depth
}

/**
 * True when the plan decompresses at `depth` immediately after `index`.
 *
 * The engine ascends to the first deco stop in one go, then re-evaluates the
 * ceiling at the arrival depth: on deep trimix profiles that re-evaluation can
 * land slightly above the stop it just chose, because stop depths are snapped
 * onto the decoStopInterval grid. The diver does stop right there and the stop
 * clears the ceiling, so that arrival is not an ascent above the ceiling.
 * Between the ascent and the stop the engine may push a gas or diluent change
 * at the very same depth, hence the loop rather than a single lookahead.
 *
 * @param {Object[]} segments - Segment list being analyzed
 * @param {number} index - Index of the ascent segment
 * @param {number} depth - Arrival depth of that ascent
 * @returns {boolean} True if a deco stop is held at that depth right after
 */
function decoStopFollows(segments, index, depth) {
  for (let i = index + 1; i < segments.length; i++) {
    const next = segments[i]
    const nextDepth = ceilingReferenceDepth(next)
    if (!Number.isFinite(nextDepth) || Math.abs(nextDepth - depth) > SAME_DEPTH_EPSILON_M) return false
    if (next.type === 'stop') return true
    if (next.type !== 'gasChange' && next.type !== 'diluentChange') return false
  }
  return false
}

/**
 * Analyze dive plan segments for safety alerts.
 *
 * @param {Object[]} segments - Plan segments to analyze
 * @param {Object} options
 * @param {Object|null} options.ccrPhase - CCR phase reference for bailout comparison
 * @param {boolean} options.isCCR - Whether this is a CCR plan (affects density threshold)
 * @param {Object} options.environment - Environment settings (gas temp, water type, altitude)
 * @param {Function} options.translateFn - Translation function (key, params) => string
 * @param {boolean} options.showNarcosis - Show narcosis/END alerts (default true)
 * @param {boolean} options.showDensity - Show gas density alerts (default true)
 * @param {boolean} options.showIcd - Show ICD risk alerts (default true)
 * @param {boolean} options.trackPerSegment - Build per-segment alert map (default false)
 * @param {Object[]} options.planWarnings - Engine warnings ({ code, details }) from result.warnings
 * @returns {{ alerts: Object[], segmentAlerts: Map<number, Object[]>|null }}
 */
export function analyzeAlerts(segments, {
  ccrPhase = null,
  isCCR = false,
  environment = {},
  translateFn = (key) => key,
  showNarcosis = true,
  showDensity = true,
  showIcd = true,
  trackPerSegment = false,
  planWarnings = []
} = {}) {
  const alerts = []
  const segmentAlerts = trackPerSegment ? new Map() : null
  const t = translateFn

  // Plan level warnings raised by the engine (result.warnings): a truncated
  // stop, a runtime limit or a fallback gas all mean the plan below is not the
  // plan the diver asked for. They belong to no single segment, so they only
  // feed the global alert list. They are pushed first: an incomplete plan has
  // to be read before any per-segment detail.
  if (!Array.isArray(planWarnings)) {
    console.error('analyzeAlerts: planWarnings must be an array, got %o, plan warnings ignored', planWarnings)
  } else {
    planWarnings.forEach((warning, i) => {
      if (!warning || typeof warning.code !== 'string' || warning.code.length === 0) {
        console.error('analyzeAlerts: plan warning #%d carries no usable code (%o), warning dropped', i, warning)
        return
      }
      const code = warning.code.startsWith(WARNING_KEY_PREFIX)
        ? warning.code.slice(WARNING_KEY_PREFIX.length)
        : warning.code
      const message = t(warning.code, warning.details || {})
      // Deduplicate on the rendered message, not on the code alone: two stops
      // truncated at two different depths carry the same code but describe two
      // different problems, and both have to reach the diver.
      if (!alerts.find(a => a.code === code && a.message === message)) {
        alerts.push({ type: 'error', code, message })
      }
    })
  }

  const ccrPpN2 = ccrPhase ? ccrPhase.ppN2 : null
  const ccrEnd = ccrPhase ? ccrPhase.end : null

  // Mix breathed by the last segment that carried one, for the ICD check.
  // Tracked while walking the list rather than read off the segment: the check
  // used to test `segment.prevFHe`, a field the engine writes nowhere, so it
  // was dead code and no plan has ever raised an ICD alert.
  let prevMix = null

  segments.forEach((s, index) => {
    const segAlerts = trackPerSegment ? [] : null
    const depth = segmentDepth(s)

    // Segments that only mark an event carry no fractions at all: the
    // diluentChange pushed by ccrPass is one (gasChange aside, which does carry
    // its new mix). They used to be defaulted to air, and a diluent change held
    // at 60 m was therefore judged as air at 60 m: END 60 m and density
    // 8.1 g/l, two critical alerts raised on a loop breathing trimix. A segment
    // without a mix now has no mix, and the checks that need one step aside.
    const mix = segmentMix(s)
    const fO2 = mix ? mix.fO2 : 0
    const fHe = mix ? mix.fHe : 0
    const fN2 = mix ? mix.fN2 : 0

    // The environment has to reach these three: gasDensity already took it,
    // but ppN2 and calcEND were left on the sea level default, so a narcosis
    // alert raised at altitude or in fresh water used the wrong pressure.
    const ppN2 = mix ? Gas.ppN2(fN2, depth, environment) : null
    const density = segmentDensity(s, environment)
    const end = s.end !== undefined
      ? s.end
      : (mix ? Gas.calcEND(depth, fO2, fHe, environment) : null)

    // Helper: push alert to global list (deduplicated) and per-segment list
    const pushAlert = (type, code, message, fields) => {
      if (trackPerSegment) {
        segAlerts.push({ type, code, ...fields })
      }
      if (!alerts.find(a => a.code === code)) {
        alerts.push({ type, code, message })
      }
    }

    // 1. ppO2 critical (hypoxia)
    if (s.ppO2 !== undefined && s.ppO2 < 0.16) {
      pushAlert('error', 'hypoxia',
        t('alert_hypoxia', { value: s.ppO2.toFixed(2) }),
        { field: 'ppO2', value: s.ppO2 })
    }

    // 2. ppO2 above the deco limit (hyperoxia critical)
    //
    // The threshold carries the same slack as Gas.MOD_TOLERANCE. That slack is
    // what lets pure O2 be named "the 6 m gas": at 6 m in sea water the real
    // ppO2 is 1.6195 bar. Without it the planner would flag as a critical error
    // the very stop it just chose, on every single plan using pure O2, and an
    // alert that always fires teaches divers to ignore alerts.
    // Anything beyond that slack is a genuine overshoot and still fires.
    if (s.ppO2 !== undefined && s.ppO2 > HYPEROXIA_LIMIT_BAR) {
      pushAlert('error', 'hyperoxia',
        t('alert_hyperoxia', { value: s.ppO2.toFixed(2) }),
        { field: 'ppO2', value: s.ppO2 })
    }

    // 3. END > 40m: beyond the hard recommendation, still a graded risk
    // factor and not a plan-invalidating condition, hence 'warning'.
    if (showNarcosis && Number.isFinite(end) && end > 40) {
      pushAlert('warning', 'end_critical',
        t('alert_end_critical', { value: Math.round(end) }),
        { field: 'end', value: end })
    }
    // 4. END > 30m warning
    else if (showNarcosis && Number.isFinite(end) && end > 30) {
      pushAlert('warning', 'end_high',
        t('alert_end_high', { value: Math.round(end) }),
        { field: 'end', value: end })
    }

    // 5. Gas density > 6.2 (very high CO2 retention risk): graded risk
    // factor, not a plan-invalidating condition, hence 'warning'.
    if (showDensity && Number.isFinite(density) && density > GAS_DENSITY_CRITICAL) {
      pushAlert('warning', 'density_critical',
        t('alert_density_critical', { value: density.toFixed(1) }),
        { field: 'density', value: density })
    }
    // 6. Gas density > 5.2 warning (CCR only - higher CO2 retention risk with rebreathers)
    else if (showDensity && isCCR && Number.isFinite(density) && density > GAS_DENSITY_WARNING) {
      pushAlert('warning', 'density_high',
        t('alert_density_high', { value: density.toFixed(1) }),
        { field: 'density', value: density })
    }

    // 7. Narcosis increase on bailout (ppN2 bailout > ppN2 CCR)
    if (showNarcosis && ccrPpN2 !== null && ppN2 !== null && ppN2 > ccrPpN2 + 0.5) {
      pushAlert('warning', 'narcosis_increase',
        t('alert_narcosis_increase', { ccrValue: ccrPpN2.toFixed(2), value: ppN2.toFixed(2) }),
        { field: 'ppN2', value: ppN2, ccrValue: ccrPpN2 })
    }

    // 8. END increase on bailout (END bailout > END CCR)
    if (showNarcosis && ccrEnd !== null && Number.isFinite(end) && end > ccrEnd + 5) {
      pushAlert('warning', 'end_increase',
        t('alert_end_increase', { ccrValue: Math.round(ccrEnd), value: Math.round(end) }),
        { field: 'end', value: end, ccrValue: ccrEnd })
    }

    // 9. ICD risk: the breathing mix swaps helium for nitrogen.
    //
    // Two conditions, and the second one is what makes the check usable on a
    // rebreather. The helium fraction of a CCR loop falls continuously as the
    // diver ascends, with no gas change at all: on a 10/70 diluent at a 1.0 bar
    // setpoint it goes from 0.53 at 21 m to 0.01 at the surface, so a drop
    // threshold alone would fire on every CCR ascent ever planned. What
    // counterdiffusion needs is helium leaving WHILE nitrogen arrives, and a
    // plain ascent shrinks both inert fractions together - the last leg above
    // loses 29 points of helium and 8 points of nitrogen. Requiring the
    // nitrogen to rise keeps that depth driven drift out and leaves only the
    // real mix changes: a deco gas switch, a diluent change, a loop flush.
    //
    // The check is no longer restricted to `type === 'stop'`. A diluent switch
    // or a flush happens where the diver decides, not only at a stop, and the
    // helium swap is the same wherever it happens.
    if (showIcd && mix && prevMix
        && fHe < prevMix.fHe - ICD_HELIUM_DROP && fN2 > prevMix.fN2) {
      pushAlert('warning', 'icd_risk',
        t('alert_icd_risk', { prevValue: Math.round(prevMix.fHe * 100), value: Math.round(fHe * 100) }),
        { field: 'gas', value: fHe, prevValue: prevMix.fHe })
    }

    // 10. Ceiling violation: the engine stored, on every segment, the deco
    // ceiling reached at the end of that segment. When the profile holds a
    // depth shallower than that ceiling, the diver is decompressing above his
    // obligation: multi-level profiles that go up too early and repetitive
    // profiles that surface between two dives are the usual cases.
    if (Number.isFinite(s.ceiling)) {
      const refDepth = ceilingReferenceDepth(s)
      if (!Number.isFinite(refDepth)) {
        console.error('analyzeAlerts: segment #%d of type %o carries ceiling=%o but no usable reference depth, ceiling check skipped',
          index, s.type, s.ceiling)
      } else if (s.ceiling > refDepth + CEILING_TOLERANCE_M) {
        // An ascent that lands on its own deco stop is not a violation: the
        // diver stops there and the stop clears the ceiling (see decoStopFollows).
        const handledByDecoStop = s.type === 'ascent' && decoStopFollows(segments, index, refDepth)
        if (!handledByDecoStop) {
          pushAlert('error', 'ceiling_violation',
            t('alert_ceiling_violation', { ceiling: s.ceiling.toFixed(1), depth: refDepth }),
            { field: 'depth', value: s.ceiling, depth: refDepth })
        }
      }
    }

    // 11. Ceiling crossed during the ascent itself. The check above reads the
    // ceiling measured ON ARRIVAL, and a long ascent offgases on the way: a
    // 39 m ascent can cross a 26 m ceiling and still show a cleared one at
    // 21 m, so the crossing goes unseen. DivePlanner records that case on the
    // segment as it happens (ceilingBreach), the engine being the only place
    // where the tissue state at departure is known.
    if (s.ceilingBreach) {
      const breach = s.ceilingBreach
      if (!Number.isFinite(breach.ceiling) || !Number.isFinite(breach.depth)) {
        console.error('analyzeAlerts: segment #%d carries an unusable ceilingBreach (%o), ascent check skipped',
          index, breach)
      } else {
        pushAlert('error', 'ceiling_violation',
          t('alert_ceiling_violation', { ceiling: breach.ceiling.toFixed(1), depth: breach.depth }),
          { field: 'depth', value: breach.ceiling, depth: breach.depth })
      }
    }

    if (mix) prevMix = mix

    if (trackPerSegment && segAlerts.length > 0) {
      segmentAlerts.set(index, segAlerts)
    }
  })

  return { segmentAlerts, alerts }
}
